import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/shared/auth/AuthContext';
import { useOperator } from '@/shared/auth/OperatorContext';
import { IDENTITE } from '@/shared/marque';
import { LayoutDashboard, Map, Users, LogOut, Zap, Menu, X, Activity, Bell, UserCog, Settings2, Wrench, Radio, KeyRound, BookOpen, TrendingUp, Sparkles, FileText, Network, FileSignature, Building2, Link2, Search, Unplug, PackagePlus, RefreshCw } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Separator } from '@/shared/ui/separator';
import { useState, useEffect, useMemo } from 'react';
import useModules from '@/shared/auth/useModules';
import useCapacites from '@/shared/auth/useCapacites';
import { CAP } from '@/shared/auth/capacites';
import api from '@/shared/api/client';

// Sections de la barre laterale, calquees sur la version de reference
// (lvdjgroup/flexcore-4-aout-, components/Sidebar.js) : cinq groupes et un champ de
// recherche.
//
// ⚠️ ELLES NE SE REPLIENT PLUS (item M1, retour de David du 30/08). Elles l'ont fait
// jusque-la, avec un etat persiste dans le navigateur. David veut « un simple titre de
// section, plus rien qui se replie » : un titre qui a l'air cliquable et ne fait rien
// serait pire que les deux etats precedents. Le mecanisme part donc EN ENTIER —
// le bouton, l'etat ferme, le chevron, `aria-expanded` et l'enregistrement — et la
// cle laissee dans les navigateurs qui l'ont deja est effacee au chargement.
//
// POURQUOI DES SECTIONS. La notre etait PLATE sur seize entrees — dix-sept depuis que
// le lot 5 y a ajoute le bridge. Seize liens de meme poids visuel ne se parcourent
// pas, ils se relisent en entier a chaque fois : rien ne dit ou commencer a chercher.
// C'est le seul ecart d'ergonomie ressenti qui subsistait par rapport a la version du
// client.
//
// LA REPARTITION SUIT LA SIENNE, pas la notre. Les entrees qui n'existent que chez
// nous — les deux ecrans de revenus, les gabarits de mandat, le bridge — sont rangees
// dans le groupe de leur voisin naturel plutot que de fabriquer un sixieme groupe :
// une section supplementaire par entree nouvelle ramenerait la liste plate.
// CHAQUE ENTREE DECLARE LA CAPACITE QUE SA ROUTE EXIGE (`cap:`), et rien d'autre.
//
// Avant le lot I, le filtre ne connaissait que le drapeau `adminOnly` et le role
// `admin`. Une entree dont la route exige `tarifs:read` n'avait donc AUCUN moyen de le
// dire : « Facturation » et « Agregateurs » etaient proposees a tout le monde, et le
// gestionnaire cliquait sur des ecrans qui ne se remplissaient jamais. Le journal nginx
// de la pre-production en garde la trace, le 26/08 a 09:47 puis 09:48.
//
// Le correctif paresseux aurait ete de poser `adminOnly: true` sur quatre entrees de
// plus. Il serait revenu au prochain ecran ajoute, comme il etait deja revenu. Ici, la
// capacite est celle que la route exige REELLEMENT cote serveur : la source unique vit
// dans `backend/app/core/matrice_droits.py`, publiee au compte par `/api/auth/me`.
//
// ⚠️ Ce n'est PAS une protection. Le refus reste pose sur le chemin de chaque requete.
// Ce filtre decide de ce qu'on PROPOSE, jamais de ce qu'on PEUT.
//
// `temoin:` EST LA PIECE QUI REND LA DECLARATION PROUVABLE, et sans elle tout ce qui
// precede n'est qu'une copie de plus. C'est la route dont l'entree depend pour se
// remplir. L'interface ne l'appelle JAMAIS : elle sert au controle
// `test_menu_correspond_a_la_matrice.py`, qui compare `cap`/`roles` a la garde
// reellement montee par FastAPI sur cette route. Une declaration qui derive devient
// donc un rouge de CI, et non une entree de menu silencieusement fausse.
//
// Le controle a deja servi : ecrites a la main, quatre des quinze declarations
// etaient fausses (la carte exige `dashboard:read` et non `clients:read`, les
// alertes `monitoring:read` et rien, l'identite et les gabarits sont gardes par un
// ROLE et non par une capacite). Deux d'entre elles marchaient par accident.
const NAV_SECTIONS = [
  {
    cle: 'general',
    libelle: 'Général',
    items: [
      { to: '/dashboard', label: 'Tableau de bord', icon: LayoutDashboard,
        cap: CAP.DASHBOARD_READ, temoin: 'GET /api/dashboard/stats' },
      { module: 'CRM', to: '/carte', label: 'Carte des actifs', icon: Map,
        cap: CAP.DASHBOARD_READ, temoin: 'GET /api/installations' },
      { to: '/alertes', label: 'Alertes', icon: Bell, badge: true,
        cap: CAP.MONITORING_READ, temoin: 'GET /api/alerts' },
    ],
  },
  {
    cle: 'exploitation',
    libelle: 'Exploitation',
    items: [
      { module: 'CRM', to: '/clients', label: 'Clients', icon: Users,
        cap: CAP.CLIENTS_READ, temoin: 'GET /api/clients' },
      { module: 'Monitoring', to: '/monitoring', label: 'Monitoring', icon: Activity,
        cap: CAP.MONITORING_READ, temoin: 'GET /api/monitoring/assets' },
      // Item I6 : le gestionnaire SURVEILLE et ne pilote pas. Le serveur refusait
      // deja (`GET /api/pilotage/actions` = 403 le 30/08) ; l'entree se ferme
      // desormais par la MEME capacite que la route, et non par un nom de role.
      { module: 'Pilotage', to: '/pilotage', label: 'Pilotage des actifs', icon: Radio,
        cap: CAP.PILOTAGE, temoin: 'GET /api/pilotage/actions' },
      // Lot R : les dossiers dont un equipement n'a jamais repondu, et les appareils
      // qu'aucun dossier ne porte. Range sous Monitoring parce qu'on y arrive en
      // constatant qu'une machine ne remonte rien, pas en posant un chantier.
      { module: 'Monitoring', to: '/rattrapage-materiel', label: 'Rattrapage matériel',
        icon: Unplug, cap: CAP.INTEGRATIONS_MANAGE,
        temoin: 'GET /api/rattrapage-materiel/propositions' },
    ],
  },
  {
    cle: 'business',
    libelle: 'Business',
    items: [
      // Items I1 et I2 : tout ce qui touche a l'argent exige `tarifs:read`, que le
      // gestionnaire n'a plus depuis le 30/08. Declarer la capacite retire l'entree
      // de SON menu sans la retirer de celui de l'admin.
      { to: '/agregateurs', label: 'Agrégateurs', icon: Network,
        cap: CAP.TARIFS_READ, temoin: 'GET /api/aggregateurs' },
      // Symphonics et Survoltage ne sont PAS des entrees de menu : on y entre par les
      // deux cartes de l'ecran Agregateurs, comme dans la version de reference. Les
      // remettre ici donnait deux chemins vers le meme ecran, dont un qui doublonnait
      // le titre de la section.
      { to: '/bridge', label: 'Bridge agrégateurs', icon: Link2,
        cap: CAP.INTEGRATIONS_MANAGE, temoin: 'GET /api/bridge/administration/etat' },
      { module: 'Revenus', to: '/facturation', label: 'Facturation', icon: FileText,
        cap: CAP.TARIFS_READ, temoin: 'GET /api/factures' },
    ],
  },
  {
    cle: 'administration',
    libelle: 'Administration',
    items: [
      { to: '/utilisateurs', label: 'Utilisateurs', icon: UserCog,
        cap: CAP.USERS_MANAGE, temoin: 'GET /api/users' },
      // Il n'y a PAS d'entree « Droits delegues » : retiree le 03/09 avec l'ecran
      // qu'elle ouvrait. Le choix de privileges se fait dans la fiche d'un compte, sur
      // /utilisateurs — donc derriere cette entree-ci, qui exige deja `users:manage`,
      // la capacite que `/admin/delegations` reclame. Une entree de menu vers une page
      // demontee rendrait un 404 au clic.
      // `menus:write` et non `menus:read` : la LECTURE des menus sert les listes
      // deroulantes de tout formulaire client, et le gestionnaire comme les roles
      // terrain l'ont. C'est l'EDITION qui reserve cet ecran.
      { to: '/menus', label: 'Menus', icon: Settings2,
        cap: CAP.MENUS_WRITE, temoin: 'PUT /api/admin/menus/{menu_key}' },
      // Ces deux routes-la sont gardees par `require_role('admin')` cote serveur et
      // par AUCUNE capacite. L'entree le declare tel quel : leur preter
      // `integrations:manage` ou `menus:write` marchait par accident — ces deux
      // capacites n'appartiennent qu'a l'admin aujourd'hui — et aurait ouvert ces
      // ecrans au premier role qui gagnerait l'une d'elles.
      { to: '/identite', label: 'Identité', icon: Building2,
        roles: ['admin'], temoin: 'PUT /api/operator/branding' },
      { to: '/gabarits-mandat', label: 'Gabarits de mandat', icon: FileSignature,
        roles: ['admin'], temoin: 'GET /api/admin/mandat-templates' },
      // Le geste que nomment les alertes « declaration Enedis » : il doit exister.
      { to: '/rattrapage-enedis', label: 'Rattrapage Enedis', icon: RefreshCw,
        cap: CAP.BULK_EXECUTE, temoin: 'GET /api/admin/enedis/rattrapage' },
    ],
  },
  {
    cle: 'aide',
    libelle: 'Aide',
    items: [
      // Aucune route derriere : le lexique est servi par le paquet du front. Rien a
      // declarer, donc rien a refuser — et donc pas de temoin.
      { to: '/aide', label: 'Aide & Lexique', icon: BookOpen },
    ],
  },
];

const DISTRIBUTEUR_ITEMS = [
  // ⚠️ `roles` et NON `cap: CAP.SAV_READ`, alors que la capacite existe et porte le
  // bon nom. `GET /api/distributeur/sav-equipements` est gardee par
  // `require_role('admin', 'distributeur')` et par AUCUNE capacite : `sav:read`
  // n'est verifiee nulle part sur cette route. La declaration par capacite
  // marchait par accident — `sav:read` n'appartient qu'a ces deux memes roles — et
  // c'est le controle contre la matrice qui l'a trouvee, pas la relecture.
  { module: 'SAV', to: '/sav', label: 'SAV distributeur', icon: Wrench,
    roles: ['admin', 'distributeur'], temoin: 'GET /api/distributeur/sav-equipements' },
  // Juste sous le SAV, à la demande de David (01/10) : ajouter un produit que le
  // catalogue ne prévoit pas. Ouvre la fiche par-dessus la page SAV.
  { module: 'SAV', to: '/sav/produits/nouveau', label: 'Ajouter un produit', icon: PackagePlus,
    roles: ['admin', 'distributeur'], temoin: 'POST /api/distributeur/produits' },
];

// ⚠️ CETTE CLE N'EST PLUS ECRITE, elle n'est plus qu'EFFACEE (item M1).
//
// Le repli des sections l'enregistrait dans le navigateur. Le repli est parti ; la cle,
// elle, reste dans le navigateur de tous ceux qui ont deja ouvert l'application. Une
// preference qu'on ne peut plus exprimer ne doit pas continuer d'exister quelque part :
// on la retire au chargement plutot que de la laisser trainer jusqu'au jour ou quelqu'un
// la relit par erreur.
const CLE_REPLI_ABANDONNEE = 'flexcore_sidebar_sections';

// LA CAPACITE QU'EXIGE `GET /api/alerts/count` COTE SERVEUR, nommee une seule fois ici.
// Le compteur de la pastille « Alertes » ne part que pour qui la possede. C'est une copie
// de la garde serveur : `test_compteur_alertes_correspond_a_la_route.py` la compare a la
// garde reellement montee et rougit si l'une des deux change sans l'autre.
const CAP_COMPTEUR_ALERTES = CAP.MONITORING_READ;

// L'ICONE DE CHAQUE ECRAN, DERIVEE DU MENU ET DE LUI SEUL (item M2).
//
// Les cartes du tableau de bord doivent porter LA MEME icone que l'entree de menu qui
// mene au meme ecran : c'est le lien visuel que David demande, pas de jolies icones. Un
// second jeu, ecrit a la main cote tableau de bord, aurait repondu a la lettre le jour
// meme puis diverge au premier ecran ajoute au menu — exactement la panne que
// `test_menu_correspond_a_la_matrice.py` a trouvee sur les capacites.
//
// Elle est DERIVEE, jamais recopiee : ajouter une entree de menu suffit, la carte suit.
export const ICONE_PAR_ROUTE = Object.fromEntries(
  [...NAV_SECTIONS.flatMap((s) => s.items), ...DISTRIBUTEUR_ITEMS].map((i) => [i.to, i.icon]),
);

// ⚠️ La CLE reste `commercial`, le LIBELLE devient « Gestionnaire » (item I5, retour de
// David du 30/08). Renommer la cle serait une migration : elle porte les treize gardes
// du serveur et la valeur `role` des comptes deja crees en base. Ce qu'on change ici est
// ce qu'un humain LIT, jamais ce que le code compare.
const ROLE_LABELS = {
  super_admin: 'Super admin',
  admin: 'Admin',
  commercial: 'Gestionnaire',
  installateur: 'Installateur',
  poseur: 'Poseur',
  distributeur: 'Distributeur',
};

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { operator } = useOperator();
  // Le menu ne propose que ce qui est souscrit : une entree qui mene a un refus
  // fait croire a une panne. La protection reste cote serveur.
  const { souscrit } = useModules();
  // Ce que son role a le droit de faire, tel que le serveur le dit. Voir useCapacites.
  const { possede } = useCapacites();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const [recherche, setRecherche] = useState('');

  // Le menage de l'ancien etat de repli, une fois par ouverture de l'application.
  //
  // Sans lui, la cle survivrait indefiniment dans les navigateurs qui ont deja servi.
  // Elle ne rend plus aucune entree invisible — plus personne ne la lit — mais elle
  // continuerait de porter une preference que rien n'honore.
  useEffect(() => {
    try { localStorage.removeItem(CLE_REPLI_ABANDONNEE); }
    catch { /* navigation privee : il n'y avait rien a effacer */ }
  }, []);

  // Le compteur n'interroge le serveur que pour qui a le droit de lire les alertes (R9,
  // recette du 26/10) : sans la capacite, chaque page et chaque minute valaient un 403
  // au distributeur. Capacites pas encore chargees : `possede` repond non, puis l'effet
  // repart des qu'elles arrivent.
  const compteurAutorise = possede(CAP_COMPTEUR_ALERTES);
  useEffect(() => {
    if (!compteurAutorise) return undefined;
    api.get('/alerts/count').then(r => setAlertCount(r.data.total)).catch(() => {});
    const interval = setInterval(() => {
      api.get('/alerts/count').then(r => setAlertCount(r.data.total)).catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, [compteurAutorise]);

  const handleLogout = () => { logout(); navigate('/login'); };

  // Ce que l'utilisateur a le droit de voir, une fois filtre par role, par module
  // souscrit, puis par sa recherche. Les sections vides disparaissent : un titre de
  // section sans entree en dessous est un cul-de-sac.
  const sectionsVisibles = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    return NAV_SECTIONS
      .map((s) => ({
        ...s,
        items: s.items
          // Une entree qui mene a un refus fait croire a une panne : on ne la propose
          // pas. Deux filtres, deux questions distinctes -- la capacite dit ce que le
          // ROLE peut faire, le module ce que le SOUS-COMPTE a souscrit. Un admin a la
          // capacite de piloter ; si son sous-compte n'a pas pris le module Pilotage,
          // il ne pilote pas.
          .filter((i) => possede(i.cap))
          // Les rares entrees gardees par `require_role` cote serveur et par aucune
          // capacite. Le serveur melange les deux mecanismes ; l'entree le dit
          // plutot que d'inventer une capacite qui n'existe pas dans `core/authz`.
          .filter((i) => !i.roles || i.roles.includes(user?.role))
          .filter((i) => souscrit(i.module))
          .filter((i) => !q || i.label.toLowerCase().includes(q)),
      }))
      .filter((s) => s.items.length > 0);
  }, [possede, souscrit, recherche, user]);

  const rendreEntree = ({ to, label, icon: Icon, badge: showBadge }) => (
    <NavLink
      key={to}
      to={to}
      onClick={() => setOpen(false)}
      className={({ isActive }) =>
        `flex items-center gap-2.5 px-2.5 h-11 md:h-9 rounded-md text-[13px] font-medium transition-colors duration-150 ${
          isActive ? 'bg-white/15 text-white' : 'text-white/60 hover:text-white hover:bg-white/10'
        }`
      }
      data-testid={`nav-${to.replace('/', '')}`}
    >
      <Icon className="w-4 h-4 shrink-0" />
      <span className="truncate">{label}</span>
      {showBadge && alertCount > 0 && (
        <span className="ml-auto min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-semibold px-1" data-testid="alert-badge-count">
          {alertCount}
        </span>
      )}
    </NavLink>
  );

  // L'ENSEIGNE PREND LA PLACE DU NOM DE L'EDITEUR, elle ne se contente plus de le
  // doubler. L'ecran Identite reglait un nom et un logo que RIEN n'affichait, pendant
  // qu'un titre en dur annoncait notre produit a un installateur qui travaille pour un
  // autre. Le repli suit l'ordre de precision : l'enseigne reglee, le nom du
  // sous-compte, enfin l'identite de la marque qui sert la page -- une barre laterale
  // sans titre serait un defaut, pas une marque blanche.
  //
  // Le super admin en est exempt : il n'appartient a aucun sous-compte, et lui montrer
  // l'enseigne du dernier sous-compte consulte lui ferait croire qu'il y est entre.
  const enseigne = user?.role === 'super_admin'
    ? ''
    : (operator?.branding?.raison_sociale || operator?.name || '');
  const marqueNom = enseigne || IDENTITE.nom;
  const marqueLogo = user?.role === 'super_admin' ? '' : (operator?.branding?.logo_url || '');

  // LE DISTRIBUTEUR VOIT FLEXCORE, PUIS SON PROPRE NOM — demande de David, 01/10.
  // Pas l'identité de l'opérateur : le service technique d'ECONEGOCE travaille dans
  // « son » SAV, pas chez EcoFlexibilité. C'est la seule vue qui affiche notre marque :
  // partout ailleurs la marque blanche du lot A s'applique.
  const estDistributeur = user?.role === 'distributeur';
  const nomDistributeur = user?.company_name || user?.name || '';

  const navContent = (
    <div className="flex flex-col h-full" data-testid="sidebar">
      {estDistributeur ? (
      <div className="px-5 py-5 space-y-2" data-testid="distributeur-brand">
        <div className="flex items-center gap-2" data-testid="flexcore-brand">
          <div className="w-7 h-7 rounded-md bg-white/20 flex items-center justify-center shrink-0">
            <Zap className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-[15px] font-semibold tracking-tight text-white">Flexcore</span>
        </div>
        {nomDistributeur && (
          <p className="text-[12px] text-white/70 truncate" title={nomDistributeur} data-testid="distributeur-brand-nom">
            {nomDistributeur}
          </p>
        )}
      </div>
      ) : (
      <div className="px-5 py-5">
        <div className="flex items-center gap-2 min-w-0" data-testid="operator-brand">
          {marqueLogo ? (
            <img src={marqueLogo} alt="" className="w-7 h-7 rounded-md object-contain bg-white p-0.5 shrink-0" />
          ) : (
            <div className="w-7 h-7 rounded-md bg-white/20 flex items-center justify-center shrink-0">
              <Zap className="w-3.5 h-3.5 text-white" />
            </div>
          )}
          <span
            className="text-[15px] font-semibold tracking-tight text-white truncate"
            title={marqueNom}
            data-testid="operator-brand-nom"
          >
            {marqueNom}
          </span>
        </div>
      </div>
      )}

      {/* Recherche : seize entrees ne se parcourent pas a l'oeil, elles se relisent
          en entier. Le champ evite d'avoir a savoir DANS QUELLE section ranger ce
          qu'on cherche. Masque pour les roles a une seule entree, ou il n'aurait
          rien a filtrer. */}
      {user?.role !== 'distributeur' && user?.role !== 'installateur' && (
        <div className="px-3 pb-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-white/40" aria-hidden />
            <input
              type="text"
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              placeholder="Rechercher…"
              aria-label="Rechercher dans le menu"
              className="w-full bg-white/[0.06] border border-white/10 rounded-md pl-7 pr-2 h-11 text-[12px] text-white placeholder:text-white/30 focus:outline-none focus:border-white/25"
              data-testid="sidebar-search"
            />
          </div>
        </div>
      )}

      <nav className="flex-1 min-h-0 px-3 py-1 space-y-2 overflow-y-auto">
        {user?.role === 'distributeur' ? (
          DISTRIBUTEUR_ITEMS.filter((i) => possede(i.cap) && souscrit(i.module)).map(rendreEntree)
        ) : user?.role === 'installateur' ? null : (
          // Un TITRE, et rien d'autre : ni bouton, ni chevron, ni `aria-expanded`.
          // Le `data-testid` reste, les recettes navigateur ciblent les sections par
          // lui. La hauteur passe de h-11 a py-1.5 : h-11 etait la zone de TOUCHE d'un
          // bouton — quarante-quatre pixels reserves au doigt — et un titre qui ne se
          // clique plus n'a plus a la reserver. Sur telephone, c'etait cinq fois
          // quarante-quatre pixels qui repoussaient les entrees hors de l'ecran.
          //
          // La TAILLE, elle, ne bouge pas : l'item M3 porte sur le tableau de bord, pas
          // sur le menu, et grossir le menu au passage sortirait du lot.
          sectionsVisibles.map((s) => (
            <div key={s.cle}>
              <p
                className="px-2.5 py-1.5 text-[10.5px] uppercase tracking-wider text-white/40"
                data-testid={`sidebar-section-${s.cle}`}
              >
                {s.libelle}
              </p>
              <div className="mt-0.5 space-y-0.5">{s.items.map(rendreEntree)}</div>
            </div>
          ))
        )}
        {recherche.trim() && sectionsVisibles.length === 0 && (
          <p className="px-2.5 py-3 text-[12px] text-white/40" data-testid="sidebar-aucun-resultat">
            Aucune entrée ne correspond.
          </p>
        )}
      </nav>

      <div className="shrink-0 p-3 border-t border-white/10">
        <div className="flex items-center gap-2.5 px-2 py-1.5 mb-2">
          <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-[11px] font-semibold text-white">
            {user?.name?.charAt(0) || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-medium text-white truncate leading-tight">{user?.name}</p>
            <p className="text-[11px] text-white/50">{ROLE_LABELS[user?.role] || user?.role}</p>
          </div>
        </div>
        <button
          className="flex items-center gap-2 w-full px-2 h-11 md:h-8 text-[13px] text-white/40 hover:text-white rounded-md hover:bg-white/10 transition-colors"
          onClick={() => navigate('/changer-mot-de-passe')}
          data-testid="change-password-nav"
        >
          <KeyRound className="w-3.5 h-3.5" />
          Mon mot de passe
        </button>
        <button
          className="flex items-center gap-2 w-full px-2 h-11 md:h-8 text-[13px] text-white/40 hover:text-red-400 rounded-md hover:bg-white/10 transition-colors"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-3.5 h-3.5" />
          Déconnexion
        </button>
      </div>
    </div>
  );

  return (
    <>
      <button
        className="md:hidden fixed top-4 left-4 z-50 w-11 h-11 flex items-center justify-center bg-marque rounded-md shadow-sm border border-white/20 text-white"
        onClick={() => setOpen(!open)}
        data-testid="sidebar-toggle"
      >
        {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
      </button>
      {open && <div className="md:hidden fixed inset-0 bg-black/10 z-30" onClick={() => setOpen(false)} />}
      <aside className={`fixed md:static z-40 h-screen w-60 bg-marque border-r border-marque-bordure transform transition-transform duration-200 ${
        open ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
      }`}>
        {navContent}
      </aside>
    </>
  );
}
